import React, { memo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '../hooks/useDebounce';
import configServ from '../../services/config';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { EditTwoTone, Print, VisibilityTwoTone } from '@mui/icons-material';
import { Box, CircularProgress, FormControl, Grid, IconButton, InputLabel, NativeSelect, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import SearchBox from '../Search/SearchBox';
import { cellStyle, evenRowStyle, headerCellStyle, oddRowStyle, rowStyle } from '../../utility/CustomStyle';
import { rSetCustomer, rSetEnquiry } from '../../redux/reducers/GlobalVariables';
import VideosPopup from './VideosPopup';
import CheckAndReturn from '../../utility/CheckAndReturn';
import PrintSendPrescription from './PrintSendPrescription';

const PrescriptionTable = () => {
    //#region code
    const dispatch = useDispatch();
    const { company_id } = useSelector((state) => state.GlobalVariables);
    const rowsPerPageOptions = [10, 20, 50, 100];
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [count, setCount] = useState(0);
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("id");
    const [prescriptions, setPrescriptions] = useState([]);
    const [searchBy, setSearchBy] = useState("name");
    const [search, setSearch] = useState(null);
    const debounceSearch = useDebounce(search, 1000);
    const [prescriptionId, setPrescriptionId] = useState(null);
    const [prescription, setPrescription] = useState(null);

    const headers = [
        { id: "name", label: "CUSTOMER" },
        { id: "item_name", label: "PAIN AREA" },
        { id: "prescribed_by", label: "PRESCRIBED BY" },
        { id: "prescribed_at", label: "PRESCRIBED AT" },
        { id: "status", label: "STATUS" },
    ];

    const fetchQuestionList = async () => {
        try {
            setLoading(true);
            const dataToSend = {
                company_id: company_id,
                page: page,
                per_page: rowsPerPage,
                search: search,
                search_by: searchBy
            }
            const res = await configServ.getPrescriptionsList(dataToSend);
            if (res.status === 200) {
                setPrescriptions(res.data);
                setCount(res.total);
            }
            else {
                setPrescriptions([]);
                setCount(0);
            }
        }
        catch (e) {
            console.log(e);
        }
        finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchQuestionList();
    }, [page]);

    useEffect(() => {
        if (page === 0) {
            fetchQuestionList();
        }
        else {
            setPage(0);
        }
    }, [rowsPerPage, debounceSearch]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchBy = (event) => {
        try {
            setSearchBy(event.target.value);
        }
        catch (error) {
            console.log(error);
        }
    };

    const handleOpen = async (params) => {
        try {
            setPrescriptionId(params.id);
            setOpen(true);
        }
        catch (e) {
            console.log(e);
        }
    }

    const handleClose = () => {
        setOpen(false);
        setPrescriptionId(null);
    }
    
    //#endregion

    return (
        <>
            <PrintSendPrescription
                prescription={prescription}
                onClose={()=> setPrescription(null)}
            />
            <VideosPopup open={open} handleClose={handleClose} prescriptionId={prescriptionId} />

            <Box width={'100%'} sx={{ flexGrow: 1, overflow: "auto", mt: 1 }}>
                <Grid container alignItems="flex-end" justifyContent="flex-start" spacing={1}>
                    <Grid container item xs={12} md={6} justifyContent={"flex-start"}>
                        <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
                            Prescriptions: {count}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ minWidth: 120 }}>
                            <FormControl fullWidth>
                                <InputLabel variant="standard" htmlFor="uncontrolled-native">
                                    Search By
                                </InputLabel>
                                <NativeSelect
                                    value={searchBy}
                                    onChange={handleSearchBy}
                                    inputProps={{
                                        name: "searchBy",
                                        id: "uncontrolled-native",
                                    }}
                                >
                                    <option value={"name"}>Customer</option>
                                    <option value={"item_name"}>Pain Area</option>
                                </NativeSelect>
                            </FormControl>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <SearchBox search={search} setSearch={setSearch} />
                    </Grid>
                </Grid>
            </Box>

            <Box width={'100%'} sx={{ flexGrow: 1, overflow: "auto", mt: 1 }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                                {headers.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        sortDirection={orderBy === column.id ? order : false}
                                        sx={headerCellStyle}
                                    >
                                        {/* <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : "asc"}
                                            onClick={() => handleRequestSort(column.id)}
                                        > */}
                                        {column.label}
                                        {/* </TableSortLabel> */}
                                    </TableCell>
                                ))}
                                <TableCell sx={headerCellStyle}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading === false ? (
                                prescriptions.length > 0 ? (
                                    prescriptions.map((row, rowIndex) => (
                                        <TableRow
                                            key={rowIndex}
                                            sx={{
                                                ...rowStyle,
                                                ...(rowIndex % 2 === 0
                                                    ? evenRowStyle
                                                    : oddRowStyle),
                                            }}
                                        >
                                            <TableCell sx={cellStyle}>
                                                <Typography variant="body1">
                                                    {page * rowsPerPage + rowIndex + 1}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {row.name}
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {row.item_name || ""}
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {row.prescribed_by || ""}
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {CheckAndReturn.transformToDate3(row.prescribed_at)}
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                {
                                                    Boolean(row.status) === true ?
                                                        <CheckCircleIcon color="success" />
                                                        :
                                                        <CancelIcon color="error" />
                                                }
                                            </TableCell>
                                            <TableCell sx={cellStyle}>
                                                <Stack direction={"row"}>
                                                    <Tooltip title="Solutions" placement="top">
                                                        <IconButton sx={{ cursor: "pointer" }}>
                                                            <VisibilityTwoTone
                                                                color='info'
                                                                onClick={() => handleOpen(row)}
                                                                sx={{ cursor: "pointer" }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Print" placement="top">
                                                        <IconButton sx={{ cursor: "pointer" }}>
                                                            <Print
                                                                color='info'
                                                                onClick={() => setPrescription(row)}
                                                                sx={{ cursor: "pointer" }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={12}>
                                            <Box textAlign={"center"}>
                                                <Typography variant="body1" color={"red"}>
                                                    No prescription found.
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ textAlign: "center" }}>
                                        <Box>
                                            <CircularProgress />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            mt: 'auto', // Ensure the pagination stays at the bottom
                        }}
                    />
                </TableContainer>
            </Box>
        </>
    )
}

export default memo(PrescriptionTable);
